import React from "react";
import { ExclamationCircleOutlined } from "@ant-design/icons";

import Spinner from "components/ui/spinner/Spinner";
import Tooltip from "components/ui/tooltip/Tooltip";
import { pluralise } from "lib/utils";

import css from "./RecordPreviewHeading.module.scss";

type Props = {
  totalResults: number;
  displayedResults: number;
  isLoading?: boolean;
  monthlyAverageResults: number;
  recordListCountLabel?: string;
  isError?: boolean;
};

function CautionMessage({
  monthlyAverageResults,
  isLoading,
}: {
  monthlyAverageResults: number;
  isLoading: boolean;
}) {
  if (isLoading) {
    return null;
  }

  if (monthlyAverageResults > 9 && monthlyAverageResults < 210) {
    return null;
  }

  const message =
    monthlyAverageResults >= 210
      ? "These are more results than the majority of our users tend to see in their Feed per month. Consider using more specific keywords to narrow down your search and build a more relevant Feed."
      : "These are fewer results than the majority of our users tend to see in their Feed per month. Consider adding or broadening some keywords relevant to your business.";

  return (
    <Tooltip content={message} contentMaxWidth={450}>
      <div className={css.cautionMessage}>
        <ExclamationCircleOutlined /> Caution
      </div>
    </Tooltip>
  );
}

export function RecordPreviewHeading({
  totalResults,
  displayedResults,
  isLoading,
  monthlyAverageResults,
  recordListCountLabel,
  isError,
}: Props): JSX.Element {
  // A failed preview has no count to show. Reporting it as zero matches would
  // read as "your keywords are too narrow", which is the opposite of the advice
  // the user needs.
  if (isError) {
    return (
      <div className={css.recordListHeading}>
        <div className={css.recordListCount}>Couldn&apos;t load matching notices</div>
        <div className={css.filterRow}>
          <p className={css.currentResults}>
            We couldn&apos;t preview this feed. Try again, or reduce the number of keywords,
            competitors, partners and CPV codes you&apos;re previewing.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={css.recordListHeading}>
      <div className={css.recordListCount}>
        {pluralise(totalResults, "notice")} matched {recordListCountLabel}
      </div>
      <CautionMessage
        isLoading={isLoading ?? false}
        monthlyAverageResults={monthlyAverageResults}
      />
      {isLoading && <Spinner className={css.loadingSpinner} label="Loading notices" />}
      {!isLoading && (
        <div className={css.filterRow}>
          <p className={css.currentResults}>
            Showing <b>{displayedResults}</b> out of <b>{totalResults}</b> notices
          </p>
        </div>
      )}
    </div>
  );
}
